// colorRamp — authoring helper for the `ramp` param format (ParamRamp: a flat
// [t, r, g, b, t, r, g, b, ...] array, t ascending, last t should be 1.0).
// Recipes declare ramps as arrays of floats today; this lets a recipe author write
// human-readable colour stops instead of raw quads:
//
//   default: colorRamp([
//     {position: 0,   color_str: "#aaaaaa"},
//     {position: 0.5, color_str: "rgb(255,255,255)"},
//     {position: 1,   color_rgb: [255, 255, 255]},
//   ])
//
// The colour comes from exactly ONE of `color_str` (any CSS colour string parsed
// via chroma-js) or `color_rgb` (an [r, g, b] triple, 0..255 — the `ParamColor`
// convention).  Stops are emitted in position order; if the last stop's position
// isn't 1.0, a final stop at 1.0 repeats its colour (matching the UniformPacker's
// trailing-pad semantics so the GLSL rampVal terminates identically).
import chroma from "chroma-js";
import {ParamRamp} from "./@MaterialTypes.js";

export interface ColorRampStop {
	/** Ramp position in [0, 1] (the noise-value coordinate the colour is sampled at). */
	position: number;
	/** Any CSS colour string (hex / rgb() / hsl() / named). Mutually exclusive with color_rgb. */
	color_str?: string;
	/** [r, g, b] triple in 0..255 (the ParamColor convention). Mutually exclusive with color_str. */
	color_rgb?: [number, number, number];
}

function stopToRGB(stop: ColorRampStop): [number, number, number] {
	if (stop.color_str != null && stop.color_rgb != null) {
		throw new Error(`colorRamp: stop at position ${stop.position} sets both color_str and color_rgb — pick one.`);
	}
	if (stop.color_rgb != null) {
		const [r, g, b] = stop.color_rgb;
		if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255 || !Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
			throw new Error(`colorRamp: stop at position ${stop.position} has an out-of-range color_rgb ${JSON.stringify(stop.color_rgb)} (each channel must be 0..255).`);
		}
		return [r, g, b];
	}
	if (stop.color_str != null) {
		let c;
		try {
			c = chroma(stop.color_str);
		} catch {
			throw new Error(`colorRamp: stop at position ${stop.position} has an unparseable color_str "${stop.color_str}".`);
		}
		// chroma.rgb() returns ints in 0..255 already; round defensively for float inputs.
		const [r, g, b] = c.rgb();
		return [Math.round(r), Math.round(g), Math.round(b)];
	}
	throw new Error(`colorRamp: stop at position ${stop.position} needs one of color_str or color_rgb.`);
}

/** Build a ParamRamp (flat [t, r, g, b, ...] quads, 0..255 colour convention) from readable stops. */
export function colorRamp(stops: ColorRampStop[]): ParamRamp {
	if (stops.length == 0) throw new Error("colorRamp: at least one stop is required.");
	if (stops.length > 6) throw new Error(`colorRamp: at most 6 stops (the GLSL vec4 stops[6] limit); got ${stops.length}.`);
	// Emit in position order (a caller-specified order is an authoring error worth surfacing).
	const ordered = [...stops].sort((a, b) => a.position - b.position);
	const out: ParamRamp = [];
	for (const stop of ordered) {
		const t = stop.position;
		if (!Number.isFinite(t) || t < 0 || t > 1) {
			throw new Error(`colorRamp: stop position ${t} is out of [0, 1].`);
		}
		const [r, g, b] = stopToRGB(stop);
		// The ParamRamp colour convention is 0..1 floats (the UBO + GLSL rampVal consume 0..1);
		// color_rgb is authored in 0..255 so convert here.
		out.push(t, r / 255, g / 255, b / 255);
	}
	// Trailing pad: if the last stop isn't at t=1, append a repeat of its colour at 1.0
	// (mirrors UniformPacker's pad — rampVal terminates identically, no black tail).
	const last = out[out.length - 4];
	if (last < 1) {
		out.push(1, out[out.length - 3], out[out.length - 2], out[out.length - 1]);
	}
	return out;
}
